import { Component, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  confirmSignUp,
  resendSignUpCode,
  signIn,
  signOut
} from '@aws-amplify/auth';
import { CommonModule } from '@angular/common';
import { NgxSpinnerService } from 'ngx-spinner';
import { LocaleService } from 'src/app/services/locale';
import { EmailService } from 'src/app/services/email.service';
import { InviteService } from 'src/app/services/invite';
import { OtpComponent } from '../shared/otp';
import { ButtonComponent } from '../shared/button';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-verify',
  imports: [ReactiveFormsModule, CommonModule, OtpComponent, ButtonComponent],
  template: `
    <div class="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-6">
      <h1 class="text-xl sm:text-2xl lg:text-3xl font-bold futura text-[#043B22]">
        {{ locale.register_confirm_otp.heading }}
      </h1>
      <p class="text-xs sm:text-sm text-[#616161]">
        {{ locale.register_confirm_otp.subheading }}
      </p>
    </div>
    <form class="flex flex-col gap-3 sm:gap-4" [formGroup]="form" (submit)="submit()">
      <div class="flex flex-col gap-2">
        <label class="text-xs sm:text-sm text-[#616161] font-medium">
          {{ locale.register_confirm_otp.lbl_confirmation_code }}
        </label>
        <div class="flex gap-2 sm:gap-3 md:gap-4 w-full max-w-sm">
          <app-otp
            id="0"
            label="{{ locale.register_confirm_otp.lbl_confirmation_code }}"
            [control]="form.controls['otp0']"
          ></app-otp>
          <app-otp
            id="1"
            label="{{ locale.register_confirm_otp.lbl_confirmation_code }}"
            [control]="form.controls['otp1']"
          ></app-otp>
          <app-otp
            id="2"
            label="{{ locale.register_confirm_otp.lbl_confirmation_code }}"
            [control]="form.controls['otp2']"
          ></app-otp>
          <app-otp
            id="3"
            label="{{ locale.register_confirm_otp.lbl_confirmation_code }}"
            [control]="form.controls['otp3']"
          ></app-otp>
          <app-otp
            id="4"
            label="{{ locale.register_confirm_otp.lbl_confirmation_code }}"
            [control]="form.controls['otp4']"
          ></app-otp>
          <app-otp
            id="5"
            label="{{ locale.register_confirm_otp.lbl_confirmation_code }}"
            [control]="form.controls['otp5']"
          ></app-otp>
        </div>
      </div>
      <div *ngIf="error.length" class="text-red-500 text-xs sm:text-sm bg-red-50 p-3 rounded-lg">
        {{ error }}
      </div>
      <div class="mt-1 sm:mt-2">
        <app-button
          type="submit"
          [disabled]="form.invalid"
          text="{{ locale.register_confirm_otp.button_confirm }}"
        ></app-button>
      </div>
      <div class="flex w-full justify-center mt-2">
        <p
          (click)="resend()"
          class="text-xs sm:text-sm text-[#616161] hover:text-[#043B22] cursor-pointer transition-colors"
        >
          {{ locale.register_confirm_otp.button_resend_code }}
        </p>
      </div>
    </form>
    <!-- Popup -->
    <div
      *ngIf="popup"
      class="fixed inset-0 bg-black/60 flex justify-center items-center z-50 p-4"
    >
      <div
        class="p-6 sm:p-10 text-lg sm:text-2xl futura m-auto bg-white rounded-xl text-[#043B22] shadow-xl max-w-sm text-center"
      >
        {{ locale.login.resent }}
      </div>
    </div>
  `
})
export class VerifyComponent {
  constructor() {
    this.username = this.navigation?.extras?.state?.['username'];
    this.password = this.navigation?.extras?.state?.['password'];
  }

  private router: Router = inject(Router);
  private spinner = inject(NgxSpinnerService);
  private emailService = inject(EmailService);
  private inviteService = inject(InviteService);
  navigation = this.router.getCurrentNavigation();
  locale = inject(LocaleService).locale();

  error = '';
  popup = false;

  username = '';
  password = '';

  form = new FormGroup({
    otp0: new FormControl('', Validators.required),
    otp1: new FormControl('', Validators.required),
    otp2: new FormControl('', Validators.required),
    otp3: new FormControl('', Validators.required),
    otp4: new FormControl('', Validators.required),
    otp5: new FormControl('', Validators.required)
  });

  submit = async () => {
    try {
      this.spinner.show();

      const otp = Object.values(this.form.value).join('');

      const { nextStep } = await confirmSignUp({
        username: this.username,
        confirmationCode: otp
      });

      if (nextStep.signUpStep === 'DONE') {
        await signOut();
        localStorage.clear();

        const credentials = {
          username: this.username,
          password: this.password
        };

        await signIn({ ...credentials, options: { authFlowType: 'USER_PASSWORD_AUTH' } });

        // Send welcome email after successful registration
        try {
          await this.emailService.sendWelcomeEmail(this.username, {
            loginLink: `${environment.baseUrl}/login`
          });
        } catch (emailError) {
          console.error('Failed to send welcome email:', emailError);
        }

        // Check for pending invites and auto-update status to 'registered'
        try {
          const invites = await this.inviteService.loadInvitesForEmail(this.username);
          const pendingInvites = invites.filter(inv => inv.status === 'invited');
          for (const invite of pendingInvites) {
            await this.inviteService.updateInviteStatus(invite.id, 'registered');
          }
          if (invites.length > 0) {
            localStorage.setItem('isInvitedUser', 'true');
          }
        } catch (inviteError) {
          console.error('Error checking invite status on registration:', inviteError);
        }

        this.spinner.hide();

        this.router.navigateByUrl('/setup');
      }
    } catch (error) {
      this.spinner.hide();
      this.error = (error as any).message;
    }
  };

  resend = async () => {
    this.form.reset();
    this.error = '';
    this.popup = true;

    setTimeout(() => {
      this.popup = false;
    }, 2500);

    await resendSignUpCode({
      username: this.username
    });
  };

  reset = () => {
    this.error = '';
  };
}
